import { Component, forwardRef, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { interval } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { LibCoreLayoutModule } from '../../core-layout';
import {
  AbstractStorageService,
  ErrorHandlerServiceService,
} from '../../../services';
import {
  ArrayUtils,
  BusinessObjectUtils,
  CustomerUtils,
  DateTimeUtils,
} from '../../../utils';
import {
  APP_MODE,
  AvailableSlots,
  BasicAppointmentSettings,
  BusinessObject,
  DATE_FORMAT,
  Product,
} from '../../../models';
import { ControlValueAccessorComponent } from '../../../core';
import { CalendarAvailableSlotSelectionByWeekComponent } from '../calendar-available-slot-selection-by-week/calendar-available-slot-selection-by-week.component';
import { CalendarAvailableSlotSelectionByDateComponent } from '../calendar-available-slot-selection-by-date/calendar-available-slot-selection-by-date.component';
import { CalendarService } from '../services/calendar.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CalendarStorageService } from '../services/calendar-storage.service';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatRadioModule } from '@angular/material/radio';
import { PipesModule } from '../../../pipes';
import { CalendarSlotDateRange } from '../model/CalendarSlotDateRange.interface';

@Component({
  selector: 'lib-calendar-available-slot-selection-form',
  templateUrl: './calendar-available-slot-selection-form.component.html',
  styleUrls: ['./calendar-available-slot-selection-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    PipesModule,
    TranslateModule,
    ReactiveFormsModule,
    CalendarAvailableSlotSelectionByWeekComponent,
    CalendarAvailableSlotSelectionByDateComponent,
    // material design
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatRadioModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(
        () => CalendarAvailableSlotSelectionFormComponent
      ),
      multi: true,
    },
  ],
})
export class CalendarAvailableSlotSelectionFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  @Input() appointmentId: number;

  private _businessObject: BusinessObject;
  @Input() set businessObject(value: BusinessObject) {
    this._businessObject = value;

    if (value?.id) {
      this.initState(value?.id);
      this.updateAppointmentTimeSlots();
    }
    this.updateSelectedProducts();
  }
  get businessObject(): BusinessObject {
    return this._businessObject;
  }

  private _selectedProductIds: number[];
  @Input() set selectedProductIds(value: number[]) {
    if (!ArrayUtils.isSameArray(value, this._selectedProductIds)) {
      this._selectedProductIds = value;
      this.updateAppointmentTimeSlots();
      this.updateSelectedProducts();
    }
  }
  get selectedProductIds(): number[] {
    return this._selectedProductIds;
  }

  private _customDuration: number;
  @Input() set customDuration(value: number) {
    this._customDuration = value;
    this.updateAppointmentTimeSlots();
  }
  get customDuration(): number {
    return this._customDuration;
  }

  private _selectedStaffId: number;
  @Input() set selectedStaffId(value: number) {
    this._selectedStaffId = value;
    this.updateAppointmentTimeSlots();
  }
  get selectedStaffId(): number {
    return this._selectedStaffId;
  }

  public selectedStartTime: Date;

  public readonly AVAILABLE_SLOT_VIEW_MODE = {
    DATE: 'date',
    WEEK: 'week',
  };

  public readonly AVAILABLE_SLOT_TACT = [5, 15, 30, 60];

  public viewMode: FormControl;
  public slotTactControl: FormControl<number>;

  public isLoading = false;
  public isDisabled = false;
  public isOpenAvailableSlotDisplayConfig = false;

  public minDate = new Date();
  public maxDate = DateTimeUtils.addDayToDate(new Date(), 90);

  public availableSlots: AvailableSlots;
  public selectedProducts: Product[];

  private appMode: APP_MODE;

  private originalAvailableSlots: AvailableSlots;

  private selectedFromDate: string;
  private selectedToDate: string;

  private getAvailableSlotTrigger: FormControl<boolean>;

  private readonly REFRESH_TIME_INTERVAL = 60 * 1000; // refresh each 1 minute
  private readonly UPDATE_SLOT_DEBOUNCE_TIME = 50; // debounce time for update slot in ms

  private availableSlotRefreshInterval$: any;

  constructor(
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private storageService: AbstractStorageService,
    private calendarStorageService: CalendarStorageService,
    private calendarService: CalendarService
  ) {
    super();

    this.calendarStorageService.calendarMode$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((mode) => (this.appMode = mode));

    this.getAvailableSlotTrigger = new FormControl(false);
    this.getAvailableSlotTrigger.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .pipe(debounceTime(this.UPDATE_SLOT_DEBOUNCE_TIME))
      .subscribe(() => this.performFindingAppointmentTimeSlots());

    const savedViewMode =
      this.storageService.getSelectedAvailableSlotViewMode();
    this.viewMode = new FormControl(
      savedViewMode === this.AVAILABLE_SLOT_VIEW_MODE.WEEK
        ? this.AVAILABLE_SLOT_VIEW_MODE.WEEK
        : this.AVAILABLE_SLOT_VIEW_MODE.DATE
    );

    this.viewMode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => {
        this.storageService.saveSelectedAvailableSlotViewMode(v);

        const nearestDesiredAvailableTimeSlot: Date =
          CustomerUtils.getNearestTimeSlotOfDate(
            this.availableSlots,
            this.selectedStartTime
          ) ||
          this.selectedStartTime ||
          new Date();

        // child component will use availableSlots to set date/date range
        if (v === this.AVAILABLE_SLOT_VIEW_MODE.WEEK) {
          this.availableSlots = {
            ...(this.availableSlots || {}),
            from: DateTimeUtils.formatDateToString(
              DateTimeUtils.getFirstDateOfWeek(nearestDesiredAvailableTimeSlot),
              DATE_FORMAT.DATE_FORMAT_BACKEND
            ),
            to: DateTimeUtils.formatDateToString(
              DateTimeUtils.getLastDateOfWeek(nearestDesiredAvailableTimeSlot),
              DATE_FORMAT.DATE_FORMAT_BACKEND
            ),
            slotsOfDates: [],
          };
        } else {
          this.availableSlots = {
            ...(this.availableSlots || {}),
            from: DateTimeUtils.formatDateToString(
              nearestDesiredAvailableTimeSlot,
              DATE_FORMAT.DATE_FORMAT_BACKEND
            ),
            to: DateTimeUtils.formatDateToString(
              nearestDesiredAvailableTimeSlot,
              DATE_FORMAT.DATE_FORMAT_BACKEND
            ),
            slotsOfDates: [],
          };
        }
      });

    this.slotTactControl = new FormControl<number>(15);
    this.slotTactControl.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((tact) => {
        this.adaptAvailableSlotsBasingOnSlotTactAndAutoSelectNearestDesiredAvailableSlot(
          this.originalAvailableSlots,
          tact
        );
      });
  }

  public isAbleToConfigTimeSlotDisplay(): boolean {
    return (
      this.appMode !== APP_MODE.BOOKING && this.appMode !== APP_MODE.CHECKIN
    );
  }

  public selectSlotChanged(date: Date): void {
    if (date) {
      this.selectedStartTime = date;
    }

    if (
      CustomerUtils.isAvailableSlotsContainDateTime(this.availableSlots, date)
    ) {
      this._onChange(date);
    } else {
      this._onChange(null);
    }
  }

  public selectDateChanged(date: Date): void {
    const newDateToSet = date
      ? DateTimeUtils.formatDateToString(date, DATE_FORMAT.DATE_FORMAT_BACKEND)
      : null;

    if (
      !newDateToSet ||
      (newDateToSet === this.selectedFromDate &&
        newDateToSet === this.selectedToDate)
    ) {
      return;
    }

    this.selectedFromDate = newDateToSet;
    this.selectedToDate = newDateToSet;
    this.updateAppointmentTimeSlots();
  }

  public selectDateRangeChanged(dateRange: CalendarSlotDateRange): void {
    const newFromDateToSet = dateRange?.from
      ? DateTimeUtils.formatDateToString(
          dateRange?.from,
          DATE_FORMAT.DATE_FORMAT_BACKEND
        )
      : null;

    const newToDateToSet = dateRange?.to
      ? DateTimeUtils.formatDateToString(
          dateRange?.to,
          DATE_FORMAT.DATE_FORMAT_BACKEND
        )
      : null;

    if (
      !newFromDateToSet ||
      !newToDateToSet ||
      (newFromDateToSet === this.selectedFromDate &&
        newToDateToSet === this.selectedToDate)
    ) {
      return;
    }

    this.selectedFromDate = newFromDateToSet;
    this.selectedToDate = newToDateToSet;
    this.updateAppointmentTimeSlots();
  }

  public isAbleToDisplayAppointmentTimeSlots(): boolean {
    return !!this.businessObject?.id;
  }

  private isAbleToUpdateAppointmentTimeSlots(): boolean {
    return (
      this.isAbleToDisplayAppointmentTimeSlots() &&
      !this.isLoading &&
      (!!this.selectedProducts?.length || !!this.customDuration)
    );
  }

  private updateSelectedProducts(): void {
    if (!this.businessObject) {
      this.selectedProducts = [];
    } else {
      this.selectedProducts = BusinessObjectUtils.getListOfProducts(
        this.businessObject,
        this.selectedProductIds || []
      );
    }
  }

  private async updateAppointmentTimeSlots() {
    return this.getAvailableSlotTrigger.setValue(
      !this.getAvailableSlotTrigger.value
    );
  }

  private async performFindingAppointmentTimeSlots() {
    if (!this.isAbleToUpdateAppointmentTimeSlots()) {
      return;
    }

    try {
      this.isLoading = true;

      if (this.availableSlotRefreshInterval$) {
        this.availableSlotRefreshInterval$.unsubscribe();
      }

      if (
        this.selectedStartTime &&
        (!this.selectedFromDate || !this.selectedToDate)
      ) {
        this.initFromDateAndToDate(this.selectedStartTime);
      }

      this.originalAvailableSlots =
        await this.calendarService.findAvailableSlots(
          this.businessObject.id,
          this.selectedStaffId,
          this.appointmentId,
          this.customDuration ||
            BusinessObjectUtils.calculateTotalDuration(
              this.businessObject,
              this.selectedProductIds
            ),
          this.selectedFromDate,
          this.selectedToDate,
          this.selectedProductIds
        );

      this.adaptAvailableSlotsBasingOnSlotTactAndAutoSelectNearestDesiredAvailableSlot(
        this.originalAvailableSlots,
        this.slotTactControl.value
      );
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.availableSlotRefreshInterval$ = interval(this.REFRESH_TIME_INTERVAL)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => this.updateAppointmentTimeSlots());
      this.isLoading = false;
    }
  }

  private adaptAvailableSlotsBasingOnSlotTactAndAutoSelectNearestDesiredAvailableSlot(
    originalAvailableSlots: AvailableSlots,
    slotTact: number
  ): void {
    const adaptedAvailableSlots = this.adaptAvailableSlotsBasingOnSlotTact(
      originalAvailableSlots,
      slotTact
    );

    const nearestDesiredAvailableTimeSlot: Date =
      CustomerUtils.getNearestTimeSlotOfDate(
        adaptedAvailableSlots,
        this.selectedStartTime
      );

    // init selectedFromDate, selectedToDate
    if (!this.selectedFromDate || !this.selectedToDate) {
      this.initFromDateAndToDate(nearestDesiredAvailableTimeSlot || new Date());
    }

    // should fill available slots after init selectedFromDate and selectedToDate
    this.availableSlots = CustomerUtils.fillSlotsOfDate(
      adaptedAvailableSlots,
      this.selectedFromDate,
      this.selectedToDate
    );

    // nearestDesiredAvailableTimeSlot should be set after update this.availableSlots
    this.selectSlotChanged(nearestDesiredAvailableTimeSlot);
  }

  private adaptAvailableSlotsBasingOnSlotTact(
    originalAvailableSlots: AvailableSlots,
    slotTact: number
  ): AvailableSlots {
    const result: AvailableSlots = { ...(originalAvailableSlots || {}) };

    if (slotTact === 5) {
      // => add more slots if possible
      result.slotsOfDates = originalAvailableSlots?.slotsOfDates?.map(
        (slotOfDate) => {
          const newTimeSlot: string[] = [];

          slotOfDate.times?.forEach((time, index) => {
            newTimeSlot.push(time);

            // add more time slot if 2 slots have less than 15 min diff
            if (index < slotOfDate.times?.length - 1) {
              const currentTimeSlotAsMinute = this.parseTimeSlotToMinute(time);
              const nextTimeSlotAsMinute = this.parseTimeSlotToMinute(
                slotOfDate.times[index + 1]
              );

              if (nextTimeSlotAsMinute - currentTimeSlotAsMinute <= 15) {
                for (
                  let next5Min = currentTimeSlotAsMinute + 5;
                  next5Min < nextTimeSlotAsMinute;
                  next5Min += 5
                ) {
                  newTimeSlot.push(this.convertMinuteToSlot(next5Min));
                }
              }
            }
          });

          return {
            ...slotOfDate,
            times: newTimeSlot,
          };
        }
      );
    }

    if (slotTact === 30) {
      result.slotsOfDates = originalAvailableSlots?.slotsOfDates?.map(
        (slotOfDate) => {
          // keep only slots in format xx:00:00 or xx:30:00
          const newTimeSlot = slotOfDate.times?.filter(
            (t) => t.endsWith(':00:00') || t.endsWith(':30:00')
          );

          return {
            ...slotOfDate,
            times: newTimeSlot,
          };
        }
      );
    }

    if (slotTact === 60) {
      result.slotsOfDates = originalAvailableSlots?.slotsOfDates?.map(
        (slotOfDate) => {
          // keep only slots in format xx:00:00
          const newTimeSlot = slotOfDate.times?.filter((t) =>
            t.endsWith(':00:00')
          );

          return {
            ...slotOfDate,
            times: newTimeSlot,
          };
        }
      );
    }

    return result;
  }

  private initFromDateAndToDate(date: Date): void {
    if (this.viewMode.value === this.AVAILABLE_SLOT_VIEW_MODE.WEEK) {
      this.selectedFromDate = DateTimeUtils.formatDateToString(
        DateTimeUtils.getFirstDateOfWeek(date),
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
      this.selectedToDate = DateTimeUtils.formatDateToString(
        DateTimeUtils.getLastDateOfWeek(date),
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
    } else {
      this.selectedFromDate = DateTimeUtils.formatDateToString(
        date,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
      this.selectedToDate = DateTimeUtils.formatDateToString(
        date,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
    }
  }

  private async initState(boId: number) {
    if (!boId) {
      return;
    }

    try {
      const appointmentSettings: BasicAppointmentSettings =
        await this.calendarService.getOnlineBookingSettingsOfBo(boId);
      this.minDate = DateTimeUtils.getBeginOfDate(
        DateTimeUtils.addHourToDate(
          new Date(),
          appointmentSettings?.minInAdvanceHours || 0
        )
      );
      this.maxDate = DateTimeUtils.getEndOfDate(
        DateTimeUtils.addDayToDate(
          new Date(),
          appointmentSettings?.maxInAdvanceDays || 0
        )
      );
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }

  private convertMinuteToSlot(timeInMinute: number): string {
    const hour = Math.min(23, Math.floor(timeInMinute / 60));
    const minute = timeInMinute % 60;
    return `${this.getTwoDigitNumber(hour)}:${this.getTwoDigitNumber(
      minute
    )}:00`;
  }

  private getTwoDigitNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  private parseTimeSlotToMinute(slot: string): number {
    const slotStr = slot.slice(0, 5);
    const slotArr = slotStr.split(':');
    return Number(slotArr[0]) * 60 + Number(slotArr[1]);
  }

  private initSlotTactBasingOnSelectedTime(selectedTime: Date): void {
    // if selected time of appointment is e.g. 10:05 -> slot tact should be changed to 5
    const selectedMinutes = selectedTime.getMinutes();
    if (selectedMinutes % 15 > 0) {
      this.slotTactControl.setValue(5);
    }
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(value: Date) {
    if (value) {
      this.selectedStartTime = value;
      this.initSlotTactBasingOnSelectedTime(value);
    }

    if (!value && !!this.selectedStartTime) {
      // selected date time has been reset
      this.updateAppointmentTimeSlots();
    }
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
